import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  Stack,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import i18next from 'i18next';
import { useState, useMemo, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { DataVolumeUsageDiagram } from './DataVolumeUsageDiagram';
import { useCompanyPoolingQuery } from 'utils/graphql/hooks';
import type {
  CompanyPoolingQueryVariables,
  CompanyPooling,
  Maybe,
  SimPageQueryVariables,
} from 'utils/graphql/hooks';
import { dataVolumeUsedText } from 'utils/simUtils';

const COMPANY_POOLING_HEADER_ID = 'companyPoolingDescription';

interface CompanyPoolingProps {
  existingCompanyPooling?: Maybe<Omit<CompanyPooling, 'smsVolume'>[]>;
  simPageQueryParams: SimPageQueryVariables;
}

export const CompanyPoolingDisplay: FunctionComponent<CompanyPoolingProps> = ({
  existingCompanyPooling,
  simPageQueryParams,
}) => {
  const { t } = useTranslation();

  const accordionInitiallyOpen = !!simPageQueryParams.month;
  const [isOpen, setIsOpen] = useState<boolean>(accordionInitiallyOpen);
  const [selectedMonth, setSelectedMonth] = useState<CompanyPoolingQueryVariables | undefined>();

  const { data, isLoading } = useCompanyPoolingQuery(selectedMonth, {
    enabled: !!selectedMonth,
  });

  const poolingData = data?.companyPooling || existingCompanyPooling;
  const selectedMonthsDataVolume = poolingData?.length ? poolingData[0].dataVolume : undefined;

  const selectOptions = useMemo(
    () =>
      existingCompanyPooling?.map((pooling) => {
        const date = new Date(pooling.year, pooling.month - 1, 1);

        return {
          label: `${date.toLocaleString('en', { month: 'long' })} ${date.getFullYear()}`,
          value: { year: date.getFullYear(), month: date.getMonth() + 1 },
        };
      }) || [],
    [existingCompanyPooling],
  );

  // no CompanyPooling active or sim page is loading (no pooling from sim page)
  if (!selectedMonthsDataVolume?.limit || !existingCompanyPooling) {
    return null;
  }

  return (
    <Box
      alignSelf={{ base: 'center', lg: 'inherit' }}
      pb={{ base: 4, lg: 0 }}
      pr={{ base: 0, md: 8 }}
    >
      <Accordion allowToggle defaultIndex={isOpen ? [0] : []}>
        <AccordionItem border="none" minW="48">
          <AccordionButton pl="0" maxW="max-content" onClick={() => setIsOpen(!isOpen)}>
            <Heading as="h2" id={COMPANY_POOLING_HEADER_ID} size="md" whiteSpace="nowrap">
              {t('sim.companyPooling.headline')}
            </Heading>
            <AccordionIcon ml="1" />
          </AccordionButton>

          <Box
            sx={{
              '& > :first-child': {
                // chakra ui accordion prevents overflow with inline styles
                overflow: 'initial !important',
              },
            }}
          >
            <Stack as={AccordionPanel} p="0" mt="2">
              <Select
                size="md"
                onChange={(option) => setSelectedMonth(option?.value)}
                placeholder={t('sim.companyPooling.selectMonth')}
                defaultValue={selectOptions ? selectOptions[0] : undefined}
                options={selectOptions}
                hideSelectedOptions
                noOptionsMessage={() => t('sim.companyPooling.noSelectableMonths')}
                chakraStyles={{
                  dropdownIndicator: (provided) => ({ ...provided, background: 'white' }),
                  indicatorSeparator: (provided) => ({ ...provided, display: 'none' }),
                }}
              />

              {selectedMonthsDataVolume && (
                <DataVolumeUsageDiagram
                  labelId={COMPANY_POOLING_HEADER_ID}
                  dataVolumeCount={selectedMonthsDataVolume.count}
                  dataVolumeLimit={selectedMonthsDataVolume.limit}
                  isLoading={isLoading || !selectedMonthsDataVolume?.limit}
                />
              )}
            </Stack>
          </Box>
        </AccordionItem>
      </Accordion>

      {!isOpen && (
        <Box
          title={t('sim.dataVolume.exact', {
            used: Intl.NumberFormat(i18next.language).format(selectedMonthsDataVolume.count),
          })}
        >
          {dataVolumeUsedText(selectedMonthsDataVolume)}
        </Box>
      )}
    </Box>
  );
};
